import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Tabs, Tab, Paper, Button } from '@mui/material';
import { RoomService } from '../../api/services/roomService';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';

import ScheduledReportsTab from './components/ScheduledReportsTab';
import AnalyticsDashboardTab from './components/AnalyticsDashboardTab';
import OccupancyReportTab from './components/OccupancyReportTab';
import ArrivalsDeparturesTab from './components/ArrivalsDeparturesTab';
import ReservationReportTab from './components/ReservationReportTab';
import RateVarianceReportTab from './components/RateVarianceReportTab';
import CancellationReportTab from './components/CancellationReportTab';
import HousekeepingReportTab from './components/HousekeepingReportTab';
import RestaurantRevenueReportTab from './components/RestaurantRevenueReportTab';
import PnlReportTab from './components/PnlReportTab';
import InventoryReportTab from './components/InventoryReportTab';
import StaffAttendanceReportTab from './components/StaffAttendanceReportTab';
import ScheduleReportModal from './components/ScheduleReportModal';
import { Snackbar, Alert } from '@mui/material';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} id={`reports-tabpanel-${index}`} aria-labelledby={`reports-tab-${index}`} {...other}>
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default function ReportsAnalytics() {
    const { user } = useSelector((state) => state.auth);
    const tenantId = user?.tenantId || '1';
    const [tabValue, setTabValue] = useState(0);
    const [roomTypes, setRoomTypes] = useState([]);
    
    // Automation Modal State
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [scheduleRefresh, setScheduleRefresh] = useState(0);

    useEffect(() => {
        const fetchRoomTypes = async () => {
            try {
                const res = await RoomService.getRoomTypes();
                const list = Array.isArray(res) ? res : (res?.data || res?.roomTypes || []);
                setRoomTypes(list);
            } catch (err) {
                console.error("Failed to fetch room types:", err);
            }
        };
        fetchRoomTypes();
    }, [tenantId]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold">Reports & Analytics</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="contained" startIcon={<ScheduleSendIcon sx={{ fontSize: 18 }} />} onClick={() => setIsScheduleModalOpen(true)}>
                        Schedule Report
                    </Button>
                </Box>
            </Box>

            <Snackbar 
                open={!!successMessage} 
                autoHideDuration={6000} 
                onClose={() => setSuccessMessage('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setSuccessMessage('')}>{successMessage}</Alert>
            </Snackbar>

            <ScheduleReportModal 
                open={isScheduleModalOpen} 
                onClose={() => setIsScheduleModalOpen(false)} 
                tenantId={tenantId}
                onSuccess={(msg) => {
                    setSuccessMessage(msg);
                    setScheduleRefresh(prev => prev + 1);
                }}
            />

            <Box sx={{ borderBottom: 1, borderColor: '#eaecf0', mb: 3 }}>
                <Tabs variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile value={tabValue} onChange={handleTabChange}>
                    <Tab label="Scheduled Reports" />
                    <Tab label="Analytics Dashboard" />
                    <Tab label="Occupancy Report" />
                    <Tab label="Arrivals & Departures" />
                    <Tab label="Reservation Report" />
                    <Tab label="Rate Variance" />
                    <Tab label="Cancellations" />
                    <Tab label="Housekeeping" />
                    <Tab label="Restaurant Revenue" />
                    <Tab label="Financial P&L" />
                    <Tab label="Inventory Stock" />
                    <Tab label="Staff Attendance" />
                </Tabs>
            </Box>

            <Box>
                    <TabPanel value={tabValue} index={0}>
                        <ScheduledReportsTab tenantId={tenantId} refreshTrigger={scheduleRefresh} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <AnalyticsDashboardTab tenantId={tenantId} isActive={tabValue === 1} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        <OccupancyReportTab tenantId={tenantId} roomTypes={roomTypes} isActive={tabValue === 2} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={3}>
                        <ArrivalsDeparturesTab tenantId={tenantId} isActive={tabValue === 3} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={4}>
                        <ReservationReportTab tenantId={tenantId} roomTypes={roomTypes} isActive={tabValue === 4} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={5}>
                        <RateVarianceReportTab tenantId={tenantId} isActive={tabValue === 5} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={6}>
                        <CancellationReportTab tenantId={tenantId} isActive={tabValue === 6} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={7}>
                        <HousekeepingReportTab tenantId={tenantId} isActive={tabValue === 7} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={8}>
                        <RestaurantRevenueReportTab tenantId={tenantId} isActive={tabValue === 8} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={9}>
                        <PnlReportTab tenantId={tenantId} isActive={tabValue === 9} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={10}>
                        <InventoryReportTab tenantId={tenantId} isActive={tabValue === 10} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={11}>
                        <StaffAttendanceReportTab tenantId={tenantId} isActive={tabValue === 11} />
                    </TabPanel>
                </Box>
        </Box>
    );
}
